import { FileText, Loader2, X } from 'lucide-react';
import { memo, useMemo } from 'react';

const ChatInputFileAttachment = ({
  name = '',
  type,
  onClose,
  isLoading = false,
  previewMode = false,
  onPreviewClick,
  viewOnly = false,
}) => {
  const { fileNameWithoutExt, extension, hasExtension } = useMemo(() => {
    const lastDotIndex = name.lastIndexOf('.');
    const hasExt = lastDotIndex !== -1;
    return {
      fileNameWithoutExt: hasExt ? name.substring(0, lastDotIndex) : name,
      extension: hasExt ? name.substring(lastDotIndex) : '',
      hasExtension: hasExt,
    };
  }, [name]);

  const fileType = hasExtension ? extension.toLowerCase().substring(1) : type?.toLowerCase();
  const isDocFile = fileType === 'doc' || fileType === 'docx';
  const isTxtFile = fileType === 'txt';
  const isUnknownFile = !['pdf', 'doc', 'docx', 'txt'].includes(fileType ?? '');
  const isClickable = previewMode && !viewOnly && !isLoading;
  const showCloseButton = onClose && !previewMode && !viewOnly;

  const iconBgClass = isTxtFile
    ? 'bg-[#E9F1FF] dark:bg-blue-950/45'
    : isDocFile
      ? 'bg-[#E5F8FF] dark:bg-cyan-950/40'
      : isUnknownFile
        ? 'bg-[#F2F2F2] dark:bg-gray-800'
        : 'bg-[#FDEDED] dark:bg-red-950/40';
  const iconColorClass = isTxtFile
    ? 'text-[#003B99] dark:text-blue-300'
    : isDocFile
      ? 'text-[#02445A] dark:text-cyan-300'
      : isUnknownFile
        ? 'text-[#5C5C5C] dark:text-gray-400'
        : 'text-[#8E1A1A] dark:text-red-300';

  const handleCardClick = (event) => {
    if (!isClickable) return;
    onPreviewClick?.(event);
  };
  const maxCharsForName = hasExtension ? Math.max(1, 23 - extension.length) : 23;
  const visibleName =
    fileNameWithoutExt.length > maxCharsForName
      ? `${fileNameWithoutExt.slice(0, maxCharsForName)}...`
      : fileNameWithoutExt;

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleCardClick : undefined}
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCardClick(event);
              }
            }
          : undefined
      }
      className={[
        'flex min-w-[331px] max-w-[420px] items-center gap-3 rounded-2xl border border-edge bg-surface p-3 shadow-[0px_1px_3px_rgba(0,0,0,0.1)] dark:shadow-[0px_1px_3px_rgba(0,0,0,0.35)]',
        isClickable ? 'cursor-pointer hover:border-brand-600 dark:hover:border-brand-400' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isLoading ? (
        <div className="flex h-10 w-10 items-center justify-center">
          <Loader2 size={20} className="animate-spin text-content-secondary" />
        </div>
      ) : (
        <div className={['flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', iconBgClass].join(' ')}>
          <FileText size={24} className={iconColorClass} />
        </div>
      )}

      <div className="min-w-0 flex-1 text-sm text-content-secondary">
        <span className="truncate" title={name}>
          {visibleName}
        </span>
        {hasExtension ? <span>{extension}</span> : null}
      </div>

      {showCloseButton ? (
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center text-content-secondary hover:text-content"
          aria-label="Close file attachment"
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  );
};

export default memo(ChatInputFileAttachment);
